import { Collapse, Grid, LinearProgress, Typography } from '@mui/material';
import { useState } from 'react';
import { useAppSelector } from 'src/hooks';
import {
  IBulkCommentsDrawerData,
  IBulkCommentsDrawerResponse
} from '../../types/bulk-comment-drawer';
import { ICommentActionProps } from '../../types/ppg-sku-drawer.model';
import CommentInputControl from '../comments/comment-input-control';
import ProductCommentsContainer from '../comments/comment-list';
import useCommentDrawer from './comment-drawer.hook';
import DrawerHeader from './drawer-header';
import ItemHeader from './item-header';

/* eslint-disable react/require-default-props */
interface IProductWithCommentProps extends ICommentActionProps {
  product: IBulkCommentsDrawerData;
  showImage: boolean;
  isChild?: boolean;
  onCommentAdd: (comment: string) => Promise<unknown>;
}
/* eslint-enable react/require-default-props */

function ProductWithComment({
  product,
  showImage,
  isChild = false,
  onCommentAdd,
  onCommentDelete,
  onCommentUpdate
}: IProductWithCommentProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <>
      <Grid container alignItems='center' item xs={12}>
        <Grid p={1.5} item xs={12}>
          <ItemHeader
            onExpandToggleClick={() => {
              setIsExpanded(!isExpanded);
            }}
            isExpandable
            isOpen={isExpanded}
            showImage={showImage}
            name={product[`${product.key}_name`]}
            imageSize='small'
            imgSrc={product[`${product.key}_image_url`]}
            textProps={{
              fontWeight: isChild ? 400 : 700
            }}
          />
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Collapse in={isExpanded} timeout='auto'>
          <Grid p={1.5} item xs={12}>
            <CommentInputControl
              initialComment=''
              onCommentAdd={onCommentAdd}
            />
            <ProductCommentsContainer
              comments={product.comment_details ?? []}
              onCommentDelete={onCommentDelete}
              onCommentUpdate={onCommentUpdate}
            />
          </Grid>
        </Collapse>
      </Grid>
    </>
  );
}

function ProductChildrenCommentComponent({
  commentsData,
  parentCommentData,
  handleAddCommentForChildItem,
  handleDeleteCommentForChildItem,
  handleUpdateCommentForChildItem
}: {
  commentsData: IBulkCommentsDrawerResponse[];
  parentCommentData: IBulkCommentsDrawerData;
  handleAddCommentForChildItem: any;
  handleDeleteCommentForChildItem: any;
  handleUpdateCommentForChildItem: any;
}) {
  return (
    <>
      {commentsData.map((child: IBulkCommentsDrawerResponse, indexOfChild) => {
        if (child.children?.length) {
          return (
            <ProductChildrenCommentComponent
              commentsData={child.children}
              parentCommentData={parentCommentData}
              handleAddCommentForChildItem={handleAddCommentForChildItem}
              handleDeleteCommentForChildItem={handleDeleteCommentForChildItem}
              handleUpdateCommentForChildItem={handleUpdateCommentForChildItem}
            />
          );
        }
        return (
          <Grid
            item
            key={child.parent.data[`${child.parent.data.key}_name`]}
            xs={12}
            sx={{ m: 1, border: '1px solid #E6E6E6', borderRadius: 0.5 }}
          >
            <ProductWithComment
              product={child.parent.data}
              showImage={false}
              isChild
              onCommentAdd={(comment: string) =>
                handleAddCommentForChildItem(
                  comment,
                  child.parent.data[child.parent.data.key],
                  child.parent.data.key,
                  parentCommentData[parentCommentData.key]
                )
              }
              onCommentDelete={(commentId: number) =>
                handleDeleteCommentForChildItem(
                  commentId,
                  child.parent.data[child.parent.data.key],
                  parentCommentData[parentCommentData.key]
                )
              }
              onCommentUpdate={(commentId, updatedComment) =>
                handleUpdateCommentForChildItem(
                  commentId,
                  updatedComment,
                  child.parent.data[child.parent.data.key],
                  parentCommentData[parentCommentData.key]
                )
              }
            />
          </Grid>
        );
      })}
    </>
  );
}

export default function BulkCommentsDrawer() {
  const productsWithComments = useAppSelector(
    (state) => state.plannerV2.productsWithComments
  );
  const { isLoading } = useAppSelector(
    (state) => state.plannerV2.drawerViewMetadata
  );
  const {
    handleAddCommentForChildItem,
    handleAddCommentForParentItem,
    handleDeleteCommentForChildItem,
    handleDeleteCommentForParentItem,
    handleUpdateCommentForChildItem,
    handleUpdateCommentForParentItem
  } = useCommentDrawer();
  if (isLoading) {
    return <LinearProgress />;
  }
  return (
    <Grid container>
      <DrawerHeader data-testid='switch-render-container' title='Comments' />
      {productsWithComments.map((product) => (
        <Grid
          container
          item
          xs={12}
          mb={1.5}
          sx={{
            backgroundColor: '#F2F2F2',
            borderRadius: 0.5
          }}
        >
          <ProductWithComment
            key={product.parent.data[`${product.parent.data.key}_name`]}
            product={product.parent.data}
            showImage
            onCommentAdd={(comment: string) =>
              handleAddCommentForParentItem(
                comment,
                product.parent.data[product.parent.data.key],
                product.parent.data.key
              )
            }
            onCommentDelete={(commentId: number) =>
              handleDeleteCommentForParentItem(
                commentId,
                product.parent.data[product.parent.data.key],
                product.parent.data.key
              )
            }
            onCommentUpdate={(commentId, updatedComment) =>
              handleUpdateCommentForParentItem(
                commentId,
                updatedComment,
                product.parent.data[product.parent.data.key],
                product.parent.data.key
              )
            }
          />
          {product.children.length > 0 ? (
            <>
              <Grid px={1.5} item xs={12}>
                <Typography fontSize='12px' color='black' fontWeight={700}>
                  List of SKUs : {product.children.length}
                </Typography>
              </Grid>
              {product.children.length && (
                <ProductChildrenCommentComponent
                  commentsData={product.children}
                  parentCommentData={product.parent.data}
                  handleAddCommentForChildItem={handleAddCommentForChildItem}
                  handleDeleteCommentForChildItem={
                    handleDeleteCommentForChildItem
                  }
                  handleUpdateCommentForChildItem={
                    handleUpdateCommentForChildItem
                  }
                />
              )}
            </>
          ) : null}
        </Grid>
      ))}
    </Grid>
  );
}
